import { type Display, format, type Notation } from '@seek/currency';
import { useTranslations } from '@vocab/react';
import { Text } from 'braid-design-system';
import React, { type ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import { actionUpdateRecommendedSalaryRange } from 'src/app/state/modules/tealium/recommendedSalaryRange';
import VocabHtmlTags from 'src/app/utils/translation/vocabHtmlTags';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { PayType, type SuggestedSalary } from 'src/types/salaryTypes';

import translations from './.vocab';

const FORMAT_OPTIONS: {
  display: Display;
  notation: Notation;
  dropFraction: boolean;
} = {
  display: 'narrowSymbol',
  notation: 'standard',
  dropFraction: true,
};

export const PayRangeLabel = ({
  suggestedSalary,
  payType,
}: {
  suggestedSalary: SuggestedSalary | null;
  payType: string;
}) => {
  const { t } = useTranslations(translations);
  const locale = useConfig().getLocale();

  const dispatch = useDispatch();

  if (!suggestedSalary) {
    return null;
  }

  const { roleTitle, location, averageMin, averageMax, currency } =
    suggestedSalary;
  const frequencyLabels: Record<string, string> = {
    [PayType.ANNUAL_PACKAGE]: t('annually'),
    [PayType.ANNUAL_COMMISSION]: t('annually'),
    [PayType.COMMISSION_ONLY]: t('annually'),
    [PayType.HOURLY_RATE]: t('hourly'),
    [PayType.MONTHLY]: t('monthly'),
  };
  const frequencyLabel = frequencyLabels[payType];

  const minSalaryRecommended = format(averageMin, {
    locale,
    currency,
    ...FORMAT_OPTIONS,
  });

  const maxSalaryRecommended = format(averageMax, {
    locale,
    currency,
    ...FORMAT_OPTIONS,
  });

  dispatch(
    actionUpdateRecommendedSalaryRange(
      `${minSalaryRecommended}-${maxSalaryRecommended}`,
    ),
  );

  return (
    <Text>
      {t('pay-range-recommendation-text', {
        roleTitle,
        location,
        minSalary: minSalaryRecommended,
        maxSalary: maxSalaryRecommended,
        frequencyLabel,
        Strong: (children: ReactNode[]) => VocabHtmlTags.strong(children),
      })}
    </Text>
  );
};
